import {
  Avatar,
  Badge,
  Box,
  brandMark,
  Divider,
  Drawer,
  IconButton,
  Icons,
  iconSize,
  LanguageToggle,
  initialsOf,
  ListItemIcon,
  MAIN_CONTENT_ID,
  Menu,
  MenuItem,
  NavRowSkeleton,
  palette,
  quietScrollbar,
  SkipLink,
  Stack,
  TagChip,
  Tooltip,
  TOPBAR_CONTROL_HEIGHT,
  type LucideIcon,
  Typography,
  useFocusOnChange,
  visuallyHidden,
  fontStacks,
} from '@munifocus/ui';
import { useState, type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { GlobalSearch } from './GlobalSearch';
import {
  NAV_GROUPS,
  navItemState,
  navTitleKey,
  type FeatureState,
  type NavFeature,
  type NavItemState,
  type NavGroup,
  type NavItem,
} from './shell/navigation';
import { isFieldRole, mobileTabs } from './shell/mobile-tabs';
import { ScopeSwitcher } from './administration/ScopeSwitcher';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useConversations } from '../lib/chat-api';
import { useInbox } from '../lib/notifications-api';
import { useHandovers } from '../lib/teamwork-api';
import { useTicketAvailability } from '../lib/tickets-api';
import { useCostSettings } from '../lib/cost-recovery-api';
import { useModuleSwitches } from '../lib/permits-api';
import { useAnalyticsAvailability } from '../lib/analytics-api';
import { useInspectionBookingMe } from '../lib/inspection-booking-api';
import { NotificationBellMenu } from './NotificationBellMenu';
import { ChatIconButton } from './ChatIconButton';
import { SidenavProvider, useSidenav } from './shell/SidenavContext';
import { SosControl } from './safety/SosControl';
import { SafetyAlertBanner } from './safety/SafetyAlertBanner';

const LOCALE_KEY = 'gov.locale';
const TABBAR_HEIGHT = 64;
const TOPBAR_HEIGHT = 64;

/** The MuniFocus arc mark. */
function ArcMark({ width = 30 }: { width?: number }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 48 32"
      fill="none"
      aria-hidden="true"
      sx={{ width, height: (width * 32) / 48, flexShrink: 0 }}
    >
      <path
        d="M5 25 A19 19 0 0 1 43 25"
        stroke={palette.text.onDarkSoft}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <path
        d="M13 25 A11 11 0 0 1 35 25"
        stroke={brandMark.onDark}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle cx={24} cy={25} r={3.6} fill={brandMark.onDark} />
    </Box>
  );
}

/**
 * Brand lockup for the deep-teal chrome: arc mark + MuniFocus wordmark over a
 * "Government Portal" sublabel — the same two-line lockup the public and admin
 * portals use, so the three products read as one system. In the collapsed rail
 * only the mark survives; 72px has no room for a wordmark, let alone a sublabel.
 */
function HeaderBrand({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();

  return (
    <Box
      component={RouterLink}
      to="/"
      // The mark is decorative and the wordmark is hidden at both ends of the
      // range (icon-only rail, narrow phone), so the link carries its own name
      // rather than depending on text that is not always rendered. It contains
      // the visible wordmark, so the two never disagree (WCAG 2.5.3).
      aria-label={`MuniFocus ${t('gov.nav.portalName')}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        // The lockup is the one item in the header that may give up room, so it
        // is also the one that must survive doing so: without `overflow` its
        // nowrap wordmark simply painted itself over whatever sat beside it.
        minWidth: 0,
        overflow: 'hidden',
        textDecoration: 'none',
        userSelect: 'none',
        transition: 'opacity 200ms',
        '&:hover': { opacity: 0.85 },
      }}
    >
      <ArcMark width={iconOnly ? 28 : 30} />
      {!iconOnly && (
        <Box
          sx={{
            // Under ~400px the header's fixed controls (search, EN/FR, chat,
            // notifications, account) leave the lockup too little room to read
            // as one, so it steps down to the mark alone rather than truncating
            // the wordmark — or, as it did before, painting it over the search.
            display: 'none',
            '@media (min-width: 400px)': { display: 'block' },
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Typography
            component="div"
            sx={{
              fontFamily: fontStacks.display,
              fontWeight: 700,
              fontSize: 17,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              color: palette.text.onDark,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Muni
            <Box component="span" sx={{ color: brandMark.onDark }}>
              Focus
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: fontStacks.display,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: palette.text.onDarkSubtle,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('gov.nav.portalName')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * The top-right account control — avatar trigger over name, email, settings,
 * sign-out. Below `sm` it also carries the language switch, which otherwise
 * pushed the avatar past the right edge of a 390px screen on file pages.
 */
function AccountMenu({ onLang }: { onLang: (next: 'en' | 'fr') => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const scope = useOptionalScope();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;
  const name = user.displayName ?? '';

  const onSignOut = async () => {
    setAnchor(null);
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t('gov.nav.userMenu')}
        aria-haspopup="menu"
        sx={{ p: 0.5, flexShrink: 0 }}
      >
        <Avatar
          src={user.avatarUrl ?? undefined}
          alt={name}
          sx={{
            width: TOPBAR_CONTROL_HEIGHT,
            height: TOPBAR_CONTROL_HEIGHT,
            fontSize: 13,
            fontWeight: 700,
            color: palette.text.onDark,
            backgroundColor: palette.brand.accent,
          }}
        >
          {initialsOf(name) || <Icons.User size={iconSize.md} aria-hidden />}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 2 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: palette.text.primary }}>
            {name}
          </Typography>
          {user.email && (
            <Typography sx={{ fontSize: 12.5, color: palette.text.muted }}>{user.email}</Typography>
          )}
          {/* The capacity the account is acting in, here. A person can be clerk
              at one municipality and an officer at another, so the role is only
              meaningful next to the place it is held. */}
          {scope?.active && scope.active.roles.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              sx={{ flexWrap: 'wrap', mt: 1, alignItems: 'center' }}
            >
              {scope.active.roles.map((role) => (
                <TagChip key={role} label={t(`gov.admin.roles.${role}`)} />
              ))}
              <Typography sx={{ fontSize: 12, color: palette.text.muted }}>
                {t('gov.nav.atPlace', { place: scope.active.name })}
              </Typography>
            </Stack>
          )}
        </Box>
        <Divider />
        {/* Phones only. Autonyms: each language is named in itself, so a reader
            of either can always find theirs. */}
        {(['en', 'fr'] as const).map((code) => (
          <MenuItem
            key={code}
            lang={code}
            selected={lang === code}
            aria-label={
              code === 'en' ? t('common.a11y.switchToEnglish') : t('common.a11y.switchToFrench')
            }
            onClick={() => {
              setAnchor(null);
              onLang(code);
            }}
            sx={{ display: { xs: 'flex', sm: 'none' } }}
          >
            <ListItemIcon>{lang === code && <Icons.Check size={iconSize.lg} />}</ListItemIcon>
            {code === 'en' ? 'English' : 'Français'}
          </MenuItem>
        ))}
        <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <Icons.SlidersHorizontal size={iconSize.lg} />
          </ListItemIcon>
          {t('gov.nav.settings')}
        </MenuItem>
        <Divider />
        {/* Signing out is the one destructive thing on this menu, so it carries
            the emergency tint rather than sitting flush with Settings. */}
        <MenuItem
          onClick={() => void onSignOut()}
          sx={{
            color: palette.semantic.emergency,
            '& .MuiListItemIcon-root': { color: palette.semantic.emergency },
            '&:hover': { backgroundColor: palette.semantic.emergencyTint },
            '&:focus-visible': { backgroundColor: palette.semantic.emergencyTint },
          }}
        >
          <ListItemIcon>
            <Icons.LogOut size={iconSize.lg} />
          </ListItemIcon>
          {t('common.actions.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
}

/** A single destination in the desktop rail. Collapsed, it becomes a tooltipped icon. */
/**
 * Resolves a nav item's live counter.
 *
 * Reads the same query the Messages page and the topbar icon read, so the three
 * can never disagree — and the socket updates all of them at once. Returns 0 for
 * an item with no badge, which keeps the call unconditional and so legal at the
 * top of a component.
 */
function useNavBadges(): Record<NonNullable<NavItem['badge']>, number> {
  const conversations = useConversations();
  const inbox = useInbox();
  const handovers = useHandovers();
  return {
    messages: conversations.data?.totalUnread ?? 0,
    inbox: inbox.data?.unread ?? 0,
    handovers: handovers.data?.pending ?? 0,
  };
}

function useNavBadge(badge: NavItem['badge']): number {
  const badges = useNavBadges();
  return badge ? badges[badge] : 0;
}

function RailItem({
  item,
  active,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  const badgeCount = useNavBadge(item.badge);

  const content = (
    <Box
      component={RouterLink}
      to={item.path}
      aria-current={active ? 'page' : undefined}
      // Collapsed, the label is not rendered and a Tooltip only supplies a name
      // while it is open — so the link would be nameless at rest.
      aria-label={
        badgeCount > 0
          ? t('common.chat.navUnread', { label, count: badgeCount })
          : collapsed
            ? label
            : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        borderRadius: 1.5,
        textDecoration: 'none',
        transition: 'background-color 200ms, color 200ms',
        color: active ? palette.text.onDark : palette.text.onDarkSubtle,
        backgroundColor: active ? palette.brand.accent : 'transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 0 : 1.5,
        py: 1.1,
        mx: collapsed ? 'auto' : 0,
        width: collapsed ? 40 : 'auto',
        height: collapsed ? 40 : 'auto',
        flexShrink: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: active ? palette.brand.accent : palette.background.surfaceOnDarkSoft,
          color: palette.text.onDark,
        },
      }}
    >
      {/* Collapsed, the count rides on the icon — there is no label to sit
          beside, and a rail with no indication of unread work is the reason
          someone leaves it collapsed and then misses a message. */}
      <Badge
        badgeContent={collapsed ? badgeCount : 0}
        max={9}
        sx={{
          flexShrink: 0,
          '& .MuiBadge-badge': {
            backgroundColor: palette.brand.accent,
            color: palette.text.onDark,
            border: `1px solid ${palette.brand.rail}`,
          },
        }}
      >
        <Icon size={19} strokeWidth={1.75} style={{ flexShrink: 0 }} />
      </Badge>
      {!collapsed && (
        <Box
          component="span"
          title={label}
          sx={{
            fontFamily: fontStacks.body,
            fontSize: 14,
            fontWeight: active ? 600 : 500,
            // Wraps to a second line rather than ending in an ellipsis, as the
            // admin rail does: French labels ("Recouvrement des coûts",
            // "Tableau de bord du greffier") are wider than the rail, and a
            // destination you cannot read is not one you can choose. The row
            // centres the icon against both lines. `minWidth: 0` lets the flex
            // item shrink so the text wraps instead of spilling past the row.
            minWidth: 0,
            lineHeight: 1.3,
            overflowWrap: 'break-word',
          }}
        >
          {label}
        </Box>
      )}
      {!collapsed && badgeCount > 0 && (
        <Box
          component="span"
          // The accessible name already carries the count via the link's label,
          // so this is decoration to a screen reader.
          aria-hidden
          sx={{
            ml: 'auto',
            flexShrink: 0,
            minWidth: 20,
            px: 0.75,
            borderRadius: 10,
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '20px',
            backgroundColor: active ? palette.text.onDark : palette.brand.accent,
            color: active ? palette.brand.accent : palette.text.onDark,
          }}
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </Box>
      )}
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={label} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }
  return content;
}

/** Where the rail remembers what it last showed, between reloads. */
const NAV_MEMORY_KEY = 'gov.nav.visible';

interface RememberedRail {
  /** Whose rail, and in which scope (`kind:id`). */
  user: string;
  scope: string;
  /** The item keys it showed once everything had resolved. */
  visible: string[];
}

/**
 * Reads never throw: storage can be blocked or full, and a missing memory only
 * means placeholder rows for a moment, not a broken rail. Not sensitive — which
 * screens an account is offered, nothing about a file — so localStorage, keyed
 * by user so the next person on a shared workstation is not shown this one's.
 */
function readRememberedRail(): RememberedRail | null {
  try {
    const raw = localStorage.getItem(NAV_MEMORY_KEY);
    return raw ? (JSON.parse(raw) as RememberedRail) : null;
  } catch {
    return null;
  }
}

function rememberRail(value: RememberedRail): void {
  try {
    localStorage.setItem(NAV_MEMORY_KEY, JSON.stringify(value));
  } catch {
    // See `readRememberedRail`.
  }
}

/**
 * How the rail should treat each item for this account: show, hide, or hold
 * its place while the module switch deciding it is still in flight.
 *
 * Uses the optional scope hook because the shell also renders in isolated
 * component tests with no provider; there, nothing is hidden, which keeps the
 * tests testing the shell rather than the access model.
 */
function useNavItemState(): (item: NavItem) => NavItemState {
  const scope = useOptionalScope();
  const { pathname } = useLocation();
  // Modules a municipality switches on. Each is asked only of an account that
  // could use it, so nobody else pays for the request.
  const ready = !!scope && !scope.isLoading;
  const tickets = useTicketAvailability(ready && !!scope?.can('ticket:read'));
  const activeLg = scope?.active?.kind === 'local_government' ? scope.active.id : undefined;
  const costRecovery = useCostSettings(activeLg, !!scope?.can('cost:read'));
  // Permits and inspections: the plan's module switches at the active municipality.
  const modules = useModuleSwitches();
  // Analytics: on at the active municipality, or — working for a contractor —
  // at any municipality in reach.
  const analytics = useAnalyticsAvailability(
    ready && !!(scope?.can('analytics:read') || scope?.can('analytics:benchmark')),
  );
  const analyticsOn = activeLg
    ? (analytics.data?.localGovernmentIds.includes(activeLg) ?? false)
    : (analytics.data?.localGovernmentIds.length ?? 0) > 0;
  // Re-inspection booking: on at any municipality the officer works for.
  const booking = useInspectionBookingMe(
    ready && !!(scope?.can('file:read') || scope?.can('file:create')),
  );
  const bookingOn = (booking.data?.enabledLocalGovernmentIds.length ?? 0) > 0;

  // Until the session lands nobody knows which switches to ask, so every
  // module is pending — not off. Off was what made them pop in late.
  const sessionPending = !!scope && scope.isLoading;
  const state = (pending: boolean, on: boolean): FeatureState =>
    sessionPending || pending ? 'pending' : on ? 'on' : 'off';
  const current: Record<NavFeature, FeatureState> = {
    tickets: state(tickets.isLoading, (tickets.data?.localGovernmentIds.length ?? 0) > 0),
    costRecovery: state(costRecovery.isLoading, costRecovery.data?.enabled === true),
    permits: state(modules.permitsPending, modules.permits),
    inspections: state(modules.inspectionsPending, modules.inspections),
    analytics: state(analytics.isLoading, analyticsOn),
    inspectionBooking: state(booking.isLoading, bookingOn),
  };
  // Keyed by value, so the callback below stays stable while nothing changes.
  const featureKey = JSON.stringify(current);
  const features = useMemo(
    () => JSON.parse(featureKey) as Record<NavFeature, FeatureState>,
    [featureKey],
  );

  const { user } = useAuth();
  const userId = user?.id ?? null;
  const scopeKey = scope?.active ? `${scope.active.kind}:${scope.active.id}` : null;
  const scopeView = useMemo(
    () =>
      scope
        ? {
            isLoading: scope.isLoading,
            can: scope.can,
            scopeCount: scope.scopes.length,
            operations: scope.operations,
            operationsLoading: scope.operationsLoading,
          }
        : null,
    [scope],
  );

  // What this rail looked like the last time everything had answered, for this
  // user in this scope — drawn as-is while anything is still loading. Before
  // the active scope is known, the last one this browser used is the best
  // guess (it is the one the session restores).
  const lastVisible = useMemo(() => {
    const remembered = readRememberedRail();
    if (!remembered || !userId || remembered.user !== userId) return null;
    if (scopeKey && remembered.scope !== scopeKey) return null;
    return new Set(remembered.visible);
  }, [userId, scopeKey]);

  const settled =
    !!scope && !scope.isLoading && !scope.operationsLoading && !featureKey.includes('pending');
  useEffect(() => {
    if (!settled || !scopeKey || !userId) return;
    const visible = NAV_GROUPS.flatMap((g) => g.items)
      .filter(
        (item) =>
          navItemState(item, { features, lastVisible: null, pathname: '', scope: scopeView }) ===
          'show',
      )
      .map((item) => item.key);
    rememberRail({ user: userId, scope: scopeKey, visible });
  }, [settled, scopeKey, userId, features, scopeView]);

  return useCallback(
    (item: NavItem) => navItemState(item, { features, lastVisible, pathname, scope: scopeView }),
    [features, lastVisible, pathname, scopeView],
  );
}

function Sidenav() {
  const itemState = useNavItemState();
  const isItemListed = (item: NavItem) => itemState(item) !== 'hide';
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isCollapsed, width } = useSidenav();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const onSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <Box
      component="nav"
      aria-label={t('gov.nav.sidebar')}
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        position: 'fixed',
        insetBlock: 0,
        left: 0,
        width,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: palette.brand.rail,
        color: palette.text.onDark,
        borderRight: `1px solid ${palette.border.onDarkFaint}`,
        // `hidden` on both axes: `overflowX` alone computes `overflowY` to
        // `auto`, and the rail itself became a second scroller with a white
        // default track down the dark chrome. Only the nav list scrolls.
        overflow: 'hidden',
        transition: 'width 200ms ease',
      }}
    >
      <Box
        sx={{
          height: TOPBAR_HEIGHT,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 0 : 2,
          borderBottom: `1px solid ${palette.border.onDarkFaint}`,
        }}
      >
        <HeaderBrand iconOnly={isCollapsed} />
      </Box>

      {/* Above the navigation, because it re-scopes everything below it: the
          file list, the dashboard and the map all follow the active scope. */}
      <ScopeSwitcher collapsed={isCollapsed} />

      <Box
        component="nav"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          p: 1.5,
          // Without `minHeight: 0` a flex child refuses to shrink below its
          // content, so the list never scrolled — the whole rail did.
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          ...quietScrollbar(true),
        }}
      >
        {/* A section whose every item is gated away renders nothing at all,
            heading included — a contractor's unappointed employee was being
            shown an empty "Casework" label above four missing destinations. */}
        {NAV_GROUPS.filter((group) => group.items.some(isItemListed)).map((group, index) => (
          <Box key={group.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {/* Collapsed, a heading has nowhere to render, so the sections are
                separated by a rule instead — the grouping still reads, without
                a column of truncated words. */}
            {isCollapsed ? (
              index > 0 && (
                <Box
                  sx={{
                    height: '1px',
                    my: 1,
                    mx: 1.5,
                    backgroundColor: palette.border.onDarkFaint,
                  }}
                />
              )
            ) : (
              <Box
                component="span"
                sx={{
                  px: 1.5,
                  pt: index > 0 ? 2 : 0.5,
                  pb: 0.5,
                  fontFamily: fontStacks.body,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: palette.text.onDarkGhost,
                }}
              >
                {t(group.labelKey)}
              </Box>
            )}
            {group.items.filter(isItemListed).map((item) =>
              // Its module switch has not answered yet: hold the row's place,
              // so the list does not jump when the answer lands.
              itemState(item) === 'pending' ? (
                <NavRowSkeleton key={item.key} collapsed={isCollapsed} />
              ) : (
                <RailItem
                  key={item.key}
                  item={item}
                  active={item.isActive(pathname)}
                  collapsed={isCollapsed}
                />
              ),
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderTop: `1px solid ${palette.border.onDarkFaint}`,
          p: 1.5,
        }}
      >
        <Tooltip title={isCollapsed ? t('common.actions.signOut') : ''} placement="right" arrow>
          <Box
            component="button"
            type="button"
            onClick={() => void onSignOut()}
            // Icon-only when collapsed; the Tooltip is not an accessible name.
            aria-label={isCollapsed ? t('common.actions.signOut') : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              width: isCollapsed ? 40 : 'auto',
              height: isCollapsed ? 40 : 'auto',
              flexShrink: 0,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 0 : 1.5,
              py: 1.1,
              mx: isCollapsed ? 'auto' : 0,
              border: 'none',
              background: 'transparent',
              borderRadius: 1.5,
              cursor: 'pointer',
              fontFamily: fontStacks.body,
              fontSize: 14,
              color: palette.text.onDarkSubtle,
              transition: 'background-color 200ms, color 200ms',
              '&:hover': {
                backgroundColor: palette.background.surfaceOnDarkSoft,
                color: palette.text.onDark,
              },
            }}
          >
            <Icons.LogOut size={iconSize.lg} strokeWidth={1.75} style={{ flexShrink: 0 }} />
            {!isCollapsed && (
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {t('common.actions.signOut')}
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

/** Labels sized for a fifth of a phone: the sidebar's own label is too long in French. */
const TAB_LABEL_KEY: Partial<Record<string, string>> = {
  inbox: 'gov.nav.tabs.inbox',
  frontDesk: 'gov.nav.tabs.frontDesk',
};

const tabSx = (active: boolean) => ({
  // Equal shares, and a label that may take two lines rather than an ellipsis:
  // thirteen tabs truncated to "Ca…", "Cos…", "Pub…" told nobody anything.
  flex: '1 1 0',
  minWidth: 0,
  px: 0.5,
  gap: 0.25,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  font: 'inherit',
  color: active ? palette.brand.accent : palette.text.secondary,
  transition: 'color 200ms, transform 200ms',
  '&:active': { transform: 'scale(0.98)' },
});

function TabLabel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        lineHeight: 1.15,
        fontWeight: active ? 700 : 500,
        textAlign: 'center',
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * The mobile bottom tab bar: four destinations picked for the account's role,
 * and "More" for the rest. Root screens only (hidden on detail/workflow screens).
 */
function MobileTabBar() {
  const itemState = useNavItemState();
  // The tab bar has no room for a placeholder; a pending item waits in the
  // More sheet's absence for its answer, as every item did before.
  const isItemVisible = useCallback((item: NavItem) => itemState(item) === 'show', [itemState]);
  const scope = useOptionalScope();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  // Following a link in the sheet changes the route; the sheet goes with it.
  useEffect(() => setMoreOpen(false), [pathname]);
  const field = isFieldRole(scope?.active?.roles);
  const { primary, more } = useMemo(() => mobileTabs(isItemVisible, field), [isItemVisible, field]);
  const moreItems = more.flatMap((group) => group.items);
  const moreActive = moreItems.some((item) => item.isActive(pathname));
  const badges = useNavBadges();
  const moreUnread = moreItems.reduce(
    (sum, item) => sum + (item.badge ? badges[item.badge] : 0),
    0,
  );

  return (
    <>
      <Box
        component="nav"
        aria-label={t('common.a11y.mainNavigation')}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          height: TABBAR_HEIGHT,
          px: 0.5,
          pb: 'env(safe-area-inset-bottom)',
          boxSizing: 'content-box',
          backgroundColor: palette.background.paper,
          borderTop: `1px solid ${palette.border.line}`,
        }}
      >
        {primary.map((item) => {
          const active = item.isActive(pathname);
          const Icon = item.icon;
          return (
            <Box
              key={item.key}
              component={RouterLink}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              sx={tabSx(active)}
            >
              <TabBarIcon count={item.badge ? badges[item.badge] : 0} Icon={Icon} />
              <TabLabel active={active}>{t(TAB_LABEL_KEY[item.key] ?? item.labelKey)}</TabLabel>
            </Box>
          );
        })}
        {more.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            aria-current={moreActive ? 'page' : undefined}
            aria-label={
              moreUnread > 0
                ? t('common.chat.navUnread', { label: t('gov.nav.tabs.more'), count: moreUnread })
                : undefined
            }
            sx={tabSx(moreActive)}
          >
            <TabBarIcon count={moreUnread} Icon={Icons.Menu} />
            <TabLabel active={moreActive}>{t('gov.nav.tabs.more')}</TabLabel>
          </Box>
        )}
      </Box>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} groups={more} badges={badges} />
    </>
  );
}

/**
 * The bottom sheet behind "More": every destination that did not get a tab,
 * under the same section headings as the sidebar.
 */
function MoreSheet({
  open,
  onClose,
  groups,
  badges,
}: {
  open: boolean;
  onClose: () => void;
  groups: NavGroup[];
  badges: Record<NonNullable<NavItem['badge']>, number>;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ display: { md: 'none' } }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': t('gov.nav.moreSheet'),
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80dvh',
            backgroundColor: palette.background.paper,
            pb: 'env(safe-area-inset-bottom)',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 17 }}
        >
          {t('gov.nav.moreSheet')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('common.actions.close')}>
          <Icons.X size={iconSize.lg} />
        </IconButton>
      </Box>
      <Box sx={{ overflowY: 'auto', px: 1, pb: 2, ...quietScrollbar(false) }}>
        {groups.map((group) => (
          <Box key={group.id} component="section" aria-labelledby={`more-${group.id}`}>
            <Typography
              id={`more-${group.id}`}
              component="h3"
              sx={{
                px: 1.5,
                pt: 1.5,
                pb: 0.5,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: palette.text.muted,
              }}
            >
              {t(group.labelKey)}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = item.isActive(pathname);
                const count = item.badge ? badges[item.badge] : 0;
                const label = t(item.labelKey);
                return (
                  <li key={item.key}>
                    <Box
                      component={RouterLink}
                      to={item.path}
                      aria-current={active ? 'page' : undefined}
                      aria-label={
                        count > 0 ? t('common.chat.navUnread', { label, count }) : undefined
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minHeight: 48,
                        px: 1.5,
                        borderRadius: 1.5,
                        textDecoration: 'none',
                        color: active ? palette.brand.accent : palette.text.primary,
                        backgroundColor: active ? palette.brand.accentTint : 'transparent',
                        fontWeight: active ? 600 : 500,
                        fontSize: 15,
                        transition: 'background-color 200ms',
                        '&:hover': {
                          backgroundColor: active
                            ? palette.brand.accentTint
                            : palette.background.default,
                        },
                      }}
                    >
                      <Icon size={20} strokeWidth={1.75} aria-hidden />
                      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                        {label}
                      </Box>
                      {count > 0 && (
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            minWidth: 22,
                            px: 0.75,
                            borderRadius: 11,
                            textAlign: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: '22px',
                            backgroundColor: palette.brand.accent,
                            color: palette.text.onDark,
                          }}
                        >
                          {count > 9 ? '9+' : count}
                        </Box>
                      )}
                    </Box>
                  </li>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

/** A tab's icon, badged when its destination (or anything under More) has unread work. */
function TabBarIcon({ count, Icon }: { count: number; Icon: LucideIcon }) {
  return (
    <Badge
      badgeContent={count}
      max={9}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: palette.brand.accent,
          color: palette.text.onDark,
        },
      }}
    >
      <Icon size={22} color="currentColor" aria-hidden />
    </Badge>
  );
}

/** A screen's way out, rendered in the topbar beside the page title. */
export interface BackAction {
  /** Names the destination ("Files") — it labels the control for screen readers. */
  label: string;
  onClick: () => void;
}

/**
 * The light topbar: collapse toggle and page title on the left; page actions, a
 * divider, the language toggle, and the account avatar on the right. On mobile
 * the brand lockup replaces the collapse toggle and title, since the rail is hidden.
 *
 * Screens that pass `back` trade the mobile brand lockup for a back arrow and
 * the page title: those screens also hide the tab bar, so the bar is the only
 * navigation they have, and the brand is the one thing on it nobody needs.
 */
function Topbar({
  actions,
  back,
  title,
}: {
  actions?: ReactNode;
  back?: BackAction;
  title?: string;
}) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { isCollapsed, toggleCollapse } = useSidenav();
  const lang = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as 'en' | 'fr';

  // A route can only name a *kind* of screen. Screens that know which record
  // they are showing pass the real name and it wins.
  const titleKey = navTitleKey(pathname);
  const heading = title ?? (titleKey ? t(titleKey) : null);

  // Each segment is named in the language it switches *to* (WCAG 3.1.2).
  const langSegmentLabels = {
    en: t('common.a11y.switchToEnglish'),
    fr: t('common.a11y.switchToFrench'),
  };

  const onLang = (next: 'en' | 'fr') => {
    void i18n.changeLanguage(next);
    localStorage.setItem(LOCALE_KEY, next);
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        height: TOPBAR_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 2, sm: 3 },
        // Deep teal on mobile (it carries the brand); light on desktop, where the
        // rail already supplies the dark chrome.
        backgroundColor: { xs: palette.brand.rail, md: palette.overlay.whiteSticky },
        backdropFilter: { md: 'saturate(140%) blur(8px)' },
        borderBottom: {
          xs: `1px solid ${palette.border.onDarkFaint}`,
          md: `1px solid ${palette.border.onWhite}`,
        },
      }}
    >
      {/* Mobile: brand, or the back arrow on a screen that has one. */}
      {!back && (
        <Box sx={{ display: { xs: 'flex', md: 'none' }, minWidth: 0, overflow: 'hidden' }}>
          <HeaderBrand />
        </Box>
      )}

      <Tooltip
        title={t(isCollapsed ? 'gov.nav.expandSidebar' : 'gov.nav.collapseSidebar')}
        placement="bottom"
      >
        <IconButton
          onClick={toggleCollapse}
          aria-label={t(isCollapsed ? 'gov.nav.expandSidebar' : 'gov.nav.collapseSidebar')}
          aria-expanded={!isCollapsed}
          sx={{ display: { xs: 'none', md: 'inline-flex' }, color: palette.text.secondary }}
        >
          {isCollapsed ? (
            <Icons.PanelLeft size={iconSize.lg} />
          ) : (
            <Icons.PanelLeftClose size={iconSize.lg} />
          )}
        </IconButton>
      </Tooltip>

      {back && (
        <Tooltip title={back.label} placement="bottom">
          <IconButton
            onClick={back.onClick}
            aria-label={back.label}
            sx={{
              flexShrink: 0,
              ml: { xs: -0.5, md: 0 },
              color: { xs: palette.text.onDark, md: palette.text.secondary },
            }}
          >
            <Icons.ArrowLeft size={iconSize.lg} />
          </IconButton>
        </Tooltip>
      )}

      {heading && (
        <Typography
          component="h1"
          sx={{
            // With a back arrow the title is the label on that arrow's
            // destination, so it has to be visible on phones too. Without one it
            // is desktop-only chrome — but it used to be `display: none` below
            // md, which removed it from the accessibility tree and left every
            // signed-in page with no `h1` at all on mobile. Hide it visually
            // instead, so the document outline is intact at every breakpoint
            // (WCAG 1.3.1 / 2.4.6).
            ...(back
              ? { display: 'block' }
              : {
                  ...visuallyHidden,
                  [`@media (min-width: 900px)`]: {
                    position: 'static',
                    width: 'auto',
                    height: 'auto',
                    margin: 0,
                    // Hidden, not visible: a flex item that shows its overflow
                    // cannot shrink below its text, so a long title plus page
                    // actions pushed the avatar past the edge at ~1240px.
                    overflow: 'hidden',
                    clip: 'auto',
                    clipPath: 'none',
                  },
                }),
            minWidth: 0,
            fontFamily: fontStacks.display,
            fontSize: { xs: 17, md: 18 },
            fontWeight: 600,
            color: {
              xs: back ? palette.text.onDark : palette.text.primary,
              md: palette.text.primary,
            },
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {heading}
        </Typography>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Discoverability for Cmd-K: the trigger prints the shortcut on itself. */}
      <GlobalSearch expandAt={actions ? 'lg' : 'md'} />

      {/* Desktop only. On a phone the bar already carries search, language,
          chat, notifications and the account menu, and page actions pushed the
          language toggle off the right edge — ShellLayout renders them at the
          top of the content there instead. */}
      {actions && (
        <>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {actions}
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              my: 1.5,
              display: { xs: 'none', md: 'block' },
              borderColor: { xs: palette.border.onDarkSubtle, md: palette.border.line },
            }}
          />
        </>
      )}

      {/* The toggle is tuned per surface: dark bar on mobile, light on desktop.
          Below sm it moves into the account menu: at 390px the bar's fixed
          controls left a file page's title nothing and pushed the avatar off
          the edge. */}
      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, flexShrink: 0 }}>
        <LanguageToggle
          value={lang}
          onChange={onLang}
          onDark
          size="md"
          groupLabel={t('common.a11y.languageSwitcher')}
          segmentLabels={langSegmentLabels}
        />
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
        <LanguageToggle
          value={lang}
          onChange={onLang}
          size="md"
          groupLabel={t('common.a11y.languageSwitcher')}
          segmentLabels={langSegmentLabels}
        />
      </Box>

      {/* Officer safety: hold-to-confirm SOS, wherever safety is on. */}
      <SosControl />

      {/* Same surface tuning as the language toggle: the bar is dark on mobile. */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
        <ChatIconButton onDark />
        <NotificationBellMenu onDark />
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
        <ChatIconButton />
        <NotificationBellMenu />
      </Box>

      <AccountMenu onLang={onLang} />
    </Box>
  );
}

function ShellLayout({
  children,
  actions,
  back,
  title,
  bottomBar,
  fillViewport,
}: {
  children: ReactNode;
  actions?: ReactNode;
  back?: BackAction;
  title?: string;
  bottomBar?: ReactNode;
  fillViewport?: boolean;
}) {
  const { t } = useTranslation();
  const { width } = useSidenav();
  const { pathname } = useLocation();

  // Client-side navigation leaves focus on an unmounted node, so a keyboard or
  // screen-reader user restarts from the top of the document on every route
  // change. Hand focus to `<main>` instead (WCAG 2.4.3).
  useFocusOnChange(pathname, MAIN_CONTENT_ID);

  // The mobile tab bar shows on root screens; workflow screens (which pass a
  // sticky action footer) hide it and rely on in-content back navigation.
  const showMobileTabBar = !bottomBar;

  return (
    <Box sx={{ minHeight: '100dvh', backgroundColor: palette.background.default }}>
      <SkipLink label={t('common.a11y.skipToContent')} />
      <Sidenav />

      <Box
        sx={{
          ml: { xs: 0, md: `${width}px` },
          transition: 'margin-left 200ms ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          // A definite height, not just a floor, when a child needs to claim the
          // leftover space. `minHeight` alone leaves this column content-sized,
          // so `flex: 1` on `main` has nothing to stretch into and a `flex: 1`
          // inside `main` collapses to its content — the same reason a
          // percentage height fails here.
          ...(fillViewport && { height: '100dvh' }),
        }}
      >
        <Topbar actions={actions} back={back} title={title} />

        <Box
          component="main"
          id={MAIN_CONTENT_ID}
          // `-1` keeps it out of the tab order while letting the skip link and
          // the route-change handler move focus here.
          tabIndex={-1}
          sx={{
            flex: 1,
            width: '100%',
            px: { xs: 2, sm: 3 },
            // `pt`, not `py`: a responsive `py` puts its `md` value inside a
            // media block, which then outranks the unconditional `pb` below no
            // matter which is written first — and the last field disappears
            // under the bottom bar from `md` up.
            pt: { xs: 3, md: 4 },
            pb: bottomBar ? 12 : { xs: `${TABBAR_HEIGHT + 24}px`, md: 4 },
            // Opt-in, for a screen whose content *is* the viewport — a chat
            // transcript that owns its own scrolling rather than growing the
            // page. `main` is already stretched by `flex: 1` above, so making it
            // a column lets such a child claim the leftover height with
            // `flex: 1` instead of subtracting a guess at the chrome above it.
            ...(fillViewport && { display: 'flex', flexDirection: 'column' }),
          }}
        >
          {actions && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: 1,
                mb: 2,
                flexShrink: 0,
              }}
            >
              {actions}
            </Box>
          )}
          <SafetyAlertBanner />
          {children}
        </Box>

        {bottomBar && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: { xs: 0, md: `${width}px` },
              right: 0,
              zIndex: 1100,
              // Match the main content's horizontal padding so footer actions
              // line up with the content column at sm+ rather than sitting 8px
              // further out.
              px: { xs: 2, sm: 3 },
              py: 2,
              transition: 'left 200ms ease',
              backgroundColor: palette.overlay.whiteSticky,
              backdropFilter: 'blur(8px)',
              borderTop: `1px solid ${palette.border.line}`,
            }}
          >
            {bottomBar}
          </Box>
        )}
      </Box>

      {showMobileTabBar && <MobileTabBar />}
    </Box>
  );
}

/**
 * The signed-in application chrome. An adaptive shell:
 *
 * - **Desktop (md+):** a fixed deep-teal left rail that collapses to a 72px icon
 *   strip, beside a light topbar carrying the page title, actions, language
 *   toggle, and the account avatar — matching the admin portal's shell.
 * - **Mobile (xs–sm):** the deep-teal top bar (brand · actions · language · account)
 *   over content, with a bottom tab bar on root screens. Workflow screens pass
 *   `bottomBar`, which replaces the tab bar with a sticky action footer (matching
 *   the "detail screens have no tab bar" rule in the design handoff).
 */
export function AppShell({
  children,
  actions,
  back,
  title,
  bottomBar,
  fillViewport,
}: {
  children: ReactNode;
  /** Right-aligned header controls (e.g. a "New file" button on the dashboard). */
  actions?: ReactNode;
  /** Puts a back arrow and the page title in the topbar, on every breakpoint. */
  back?: BackAction;
  /**
   * The screen's own name, overriding the one derived from the route. Pass it
   * whenever the screen knows which record it is showing: "File" names a kind of
   * page, "DU-2026-0042 · 14 Elm St" names the thing on it.
   */
  title?: string;
  bottomBar?: ReactNode;
  /**
   * Lay the content area out as a column that fills the viewport, so a child
   * marked `flex: 1` gets exactly the height left over after the topbar and
   * anything above it.
   *
   * For screens that own their scrolling — the transcript on Messages — rather
   * than screens that grow the page. Off everywhere else, because a normal page
   * *should* grow with its content.
   */
  fillViewport?: boolean;
}) {
  return (
    <SidenavProvider>
      <ShellLayout
        actions={actions}
        back={back}
        title={title}
        bottomBar={bottomBar}
        fillViewport={fillViewport}
      >
        {children}
      </ShellLayout>
    </SidenavProvider>
  );
}
